import { useEffect } from "react"
import {
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  type LoaderFunctionArgs,
} from "react-router"
import { DocsTopBar } from "~/components/DocsTopBar"
import { LegacyDocsDialog } from "~/components/LegacyDocsDialog"
import { getDocsCatalogForRequest } from "~/server/docs-catalog-store.server"
import {
  getMirroredDocPaths,
  markdownFallbackRedirect,
} from "~/server/docs-md-fallback.server"

/**
 * Docs root. The secret path prefix (`/:docsKey/docs/...`) is validated by
 * the parent `_console.$docsKey.tsx` layout, so this loader just hands the
 * catalog to nested routes via `useRouteLoaderData("routes/_console.docs")`.
 *
 * Announces the Markdown mirror of the current page via
 * `<link rel="alternate" type="text/markdown" …>` for LLM agents.
 */
export const loader = async ({ request }: LoaderFunctionArgs) => {
  // `.md` URLs without a mirror of their own land here (see
  // docs-md-fallback.server); send them to the closest real page.
  const { pathname, search } = new URL(request.url)
  const fallback = markdownFallbackRedirect(pathname, search)
  if (fallback) throw redirect(fallback.location, fallback.status)

  const catalog = await getDocsCatalogForRequest(request)
  // The whole list rather than a flag for this page: this layout loader
  // doesn't re-run on client-side navigation between docs pages.
  return { ...catalog, mirroredPaths: [...getMirroredDocPaths()] }
}

// Social preview for links to the docs. Absolute URL because crawlers don't
// resolve relative og:image values; pinned to the permanent console host so it
// also works when the docs are reached via the pine59.com/docs load balancer.
const OG_TITLE = "Pine59 Documentation"
const OG_DESCRIPTION =
  "Guides, worked examples and dataset references for Pine59 aggregated location intelligence. Measure. Understand. Build."
const OG_IMAGE = "https://console.pine59.com/og-image.png"

export default function DocsRoot() {
  const location = useLocation()
  const { mirroredPaths } = useLoaderData<typeof loader>()

  // Reset scroll to the top on every navigation. RR's <ScrollRestoration> only
  // manages window scroll, but the docs surface scrolls inside inner
  // overflow-y-auto containers (the country sidebar, the main content region,
  // guide/article bodies), which otherwise keep their scroll position when you
  // click through to a new page. Reset the window and every inner scrollport.
  useEffect(() => {
    window.scrollTo(0, 0)
    document
      .querySelectorAll<HTMLElement>(".overflow-y-auto, .overflow-auto")
      .forEach((el) => {
        el.scrollTop = 0
      })
  }, [location.pathname])

  const mdPath = `${location.pathname}.md`
  // The API reference is rendered by Scalar and owns its own chrome, so we
  // suppress the docs-section overlays on that route. The docs path now
  // carries the secret prefix, so match `/<key>/docs/api`.
  const isHttpReference = /^\/[^/]+\/docs\/api(\/|$)/.test(location.pathname)

  // The .md alternate link only makes sense on article-style pages where the
  // mirror is a faithful copy — guide bodies, methodology/technique articles,
  // concept detail pages. (The visible copy-as-markdown button was removed
  // for now — see CopyAsMarkdownButton if it comes back.)
  const rel = location.pathname.replace(/^(?:\/[^/]+)?\/docs/, "")
  const isArticlePage =
    /^\/guides\/[^/]+$/.test(rel) ||
    /^\/concepts\/(methodology|techniques|location-types|metrics)\/[^/]+$/.test(rel) ||
    /^\/datasets\/[^/]+\/(methodology|supply|location-types|location-sets)\/[^/]+$/.test(
      rel,
    ) ||
    (/^\/datasets\/[^/]+\/[^/]+$/.test(rel) && !/\/datasets$/.test(rel))
  // Only advertise a mirror that exists. Catalog-driven pages (metric
  // variants, most datasets) have none, and crawlers following the link
  // turned into ~1,400 404s a month.
  const hasMirror = mirroredPaths.includes(`/docs${rel}`)

  return (
    <div className="flex flex-col h-[calc(100vh-var(--top-bar-height))] bg-card text-card-foreground">
      {/* Self-referential canonical so query-param / trailing-slash variants
          don't split ranking. Root-relative for now (resolves against the
          serving host); at go-live it gets pinned to the final docs domain
          (CAT-1157). */}
      <link rel="canonical" href={location.pathname} />
      {/* Open Graph / Twitter card. Rendered inline like the links above so
          React 19 hoists them into <head> on both SSR and client navigation. */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Pine59" />
      <meta property="og:title" content={OG_TITLE} />
      <meta property="og:description" content={OG_DESCRIPTION} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={OG_TITLE} />
      <meta name="twitter:description" content={OG_DESCRIPTION} />
      <meta name="twitter:image" content={OG_IMAGE} />
      {/* `<link rel="alternate">` is rendered in document order; React keeps
          it in <head> via the meta-driven SSR. Works for both SSR and CSR. */}
      {!isHttpReference && isArticlePage && hasMirror && (
        <link rel="alternate" type="text/markdown" href={mdPath} />
      )}
      <DocsTopBar />
      {/* docs.unacast.com arrivals (?from-docs=<old path>), see CAT-1204. */}
      <LegacyDocsDialog />
      {/* z-0 isolates Scalar's internal high z-indices (it ships up to 99999)
          inside this stacking context so the topbar's mega-menu always sits
          above the content. */}
      <div className="relative z-0 isolate flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  )
}
